// r01 — restaurant-opening variant
//
// "PALOV & CO" — a fictional Tashkent restaurant grand opening on 08.10.2026.
// The brief is hospitality industry sophistication: think Soho House × an
// Uzbek choyxona. We open on a cream-paper backdrop with a full-bleed grand
// restaurant photograph, the name set in Marcellus, and a brass underline.
//
// Signature element: the restaurant name "PALOV & CO" reveals letter-by-
// letter — each character fades in (opacity 0 → 1) on an 80ms stagger; once
// the last character lands, a brass underline draws beneath it via
// stroke-dasharray (left → right). Total: ~1.8s. prefers-reduced-motion
// shows the full text and full underline statically.
//
// Structure:
//   1. Hero — cream backdrop, full-width restaurant photo, signature name
//      reveal, "GRAND OPENING" eyebrow, date · venue keyline
//   2. Countdown
//   3. Story — Uzbek copy about the chef's vision and the new palov xonasi
//   4. UNIQUE — Menu preview: 6 signature dishes in a 2-column grid, each
//      with name in Marcellus italic, brass divider, mono-leaning price
//   5. UNIQUE — Chef section: bordered circular portrait, brass label,
//      short bio about Bobur Karimov (20 yillik tajriba)
//   6. Gallery
//   7. Schedule (6 events, 18:00 → 23:00)
//   8. Map ("Mustaqillik ko'chasi 47 · 'Palov & Co' restorani")
//   9. RSVP — "Bizning birinchi mehmonimiz bo'ling"
//  10. Guestbook
//  11. Share
//  12. Footer
//
// Sound: 5 triggers — `chime` once when the menu first appears (a small
// dinner-bell tap), `tap` on menu-row hover/click, `pop` on RSVP yes-confirm,
// `click` on the music toggle, `hover` on the chef-card focus. All gated
// through useSound's persisted preference and prefers-reduced-motion.
//
// Music: a small floating "Do'mbira" toggle in the bottom-right corner.
// We don't ship an audio file — instead, when on, the toggle plays a tiny
// looping arpeggio synthesised through the same Web Audio module so the
// invitation has Uzbek folk-flavoured ambient theme without a network hit.

import { useCallback, useEffect, useRef, useState } from "react";
import type { ReactNode } from "react";
import { useSound } from "@/lib/sound";
import { useT } from "@/lib/i18n";
import {
  CountdownSection,
  StorySection,
  GallerySection,
  ScheduleSection,
  MapSection,
  RsvpSection,
  GuestbookSection,
  ShareSection,
  GuestFooter,
} from "@/components/sections";
import type { VariantProps } from "./types";

// ─── prefers-reduced-motion hook ──────────────────────────────────────────

function usePrefersReducedMotion(): boolean {
  const [reduce, setReduce] = useState<boolean>(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return false;
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  });
  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return;
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const onChange = (e: MediaQueryListEvent) => setReduce(e.matches);
    mq.addEventListener?.("change", onChange);
    return () => mq.removeEventListener?.("change", onChange);
  }, []);
  return reduce;
}

// ─── IntersectionObserver-driven reveal ───────────────────────────────────

interface RevealProps {
  children: ReactNode;
  delay?: number;
  reduce: boolean;
  /** Fired exactly once when the element first crosses the threshold. */
  onShow?: () => void;
}

function Reveal({ children, delay = 0, reduce, onShow }: RevealProps) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [shown, setShown] = useState<boolean>(reduce);
  const fired = useRef(false);

  useEffect(() => {
    if (reduce) {
      setShown(true);
      if (!fired.current) {
        fired.current = true;
        onShow?.();
      }
      return;
    }
    const el = ref.current;
    if (!el || typeof IntersectionObserver === "undefined") {
      setShown(true);
      if (!fired.current) {
        fired.current = true;
        onShow?.();
      }
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setShown(true);
            if (!fired.current) {
              fired.current = true;
              onShow?.();
            }
            io.disconnect();
            break;
          }
        }
      },
      { threshold: 0.18, rootMargin: "0px 0px -8% 0px" },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [reduce, onShow]);

  return (
    <div
      ref={ref}
      style={{
        opacity: shown ? 1 : 0,
        transform: shown ? "translateY(0)" : "translateY(16px)",
        transition: reduce
          ? "none"
          : `opacity 1.1s ease-out ${delay}ms, transform 1.1s ease-out ${delay}ms`,
        willChange: reduce ? undefined : "opacity, transform",
      }}
    >
      {children}
    </div>
  );
}

// ─── SIGNATURE — letter-by-letter name reveal ────────────────────────────
//
// 80ms per character, fade only (no transform — the brief calls for a slow
// "lighting up" feel). Once the last character has landed (offset = N*80ms
// + a small grace period), the brass underline draws from left to right
// via stroke-dasharray. Reduced-motion shows the static end state.

function SignatureName({
  text,
  brass,
  ink,
  font,
  reduce,
}: {
  text: string;
  brass: string;
  ink: string;
  font: string;
  reduce: boolean;
}) {
  const chars = Array.from(text);
  // Per-character stagger
  const stagger = 80;
  // The underline starts after the last character has begun its fade — we
  // use ~80ms grace + a 600ms draw, landing the whole sequence under 1.8s
  // for an 8-character name (8*80 + 80 + 600 = 1320ms; for longer names
  // we still stay below 1.8s for the 10-char "PALOV & CO").
  const underlineDelay = chars.length * stagger + 80;
  const underlineDur = 600;

  return (
    <span
      style={{
        position: "relative",
        display: "inline-block",
        fontFamily: font,
        fontWeight: 400,
        fontSize: "clamp(56px, 11vw, 132px)",
        lineHeight: 1,
        letterSpacing: "0.04em",
        color: ink,
        // Allow the underline SVG to escape the text box.
        paddingBottom: "0.18em",
      }}
    >
      {chars.map((ch, i) => {
        // Spaces still occupy width but don't animate visibly.
        const isSpace = ch === " ";
        return (
          <span
            key={`${ch}-${i}`}
            style={{
              display: "inline-block",
              opacity: reduce ? 1 : 0,
              animation: reduce
                ? "none"
                : `palovCharIn 360ms ease-out ${i * stagger}ms forwards`,
              whiteSpace: isSpace ? "pre" : undefined,
            }}
            aria-hidden={i > 0 ? true : undefined}
          >
            {ch}
          </span>
        );
      })}
      {/* Brass underline — stroke-dasharray draws it left → right. */}
      <svg
        aria-hidden
        viewBox="0 0 100 4"
        preserveAspectRatio="none"
        style={{
          position: "absolute",
          left: 0,
          right: 0,
          bottom: 0,
          width: "100%",
          height: 4,
          overflow: "visible",
        }}
      >
        <line
          x1="0"
          y1="2"
          x2="100"
          y2="2"
          stroke={brass}
          strokeWidth="1.2"
          strokeLinecap="round"
          vectorEffect="non-scaling-stroke"
          style={{
            strokeDasharray: 100,
            strokeDashoffset: reduce ? 0 : 100,
            animation: reduce
              ? "none"
              : `palovUnderline ${underlineDur}ms ease-out ${underlineDelay}ms forwards`,
          }}
        />
      </svg>
      {/* Screen-reader-only full text for the rare case the per-char nodes
          confuse a reader. */}
      <span style={{ position: "absolute", width: 1, height: 1, overflow: "hidden", clip: "rect(0 0 0 0)" }}>
        {text}
      </span>
    </span>
  );
}

// ─── Music toggle — synthesised do'mbira-flavoured ambient loop ─────────
//
// We don't ship an audio file. When the user enables, a small interval
// schedules a 5-note pentatonic phrase (D-E-G-A-B) on a triangle oscillator
// with a soft pluck envelope, repeating every 4 seconds. It's quiet, on-
// brand, and gated by the same useSound preference (so muting kills it).

const DOMBIRA_NOTES = [293.66, 329.63, 392.0, 440.0, 493.88];

function useDombiraTheme(enabled: boolean) {
  const ctxRef = useRef<AudioContext | null>(null);
  const timerRef = useRef<number | null>(null);

  useEffect(() => {
    if (!enabled) {
      if (timerRef.current !== null) {
        window.clearInterval(timerRef.current);
        timerRef.current = null;
      }
      // Suspend the context so iOS doesn't keep it alive in the background.
      if (ctxRef.current && ctxRef.current.state === "running") {
        void ctxRef.current.suspend().catch(() => undefined);
      }
      return;
    }

    let cancelled = false;
    const start = async () => {
      if (!ctxRef.current) {
        const Ctor =
          window.AudioContext ||
          (window as unknown as { webkitAudioContext?: typeof AudioContext })
            .webkitAudioContext;
        if (!Ctor) return;
        try {
          ctxRef.current = new Ctor();
        } catch {
          return;
        }
      }
      const c = ctxRef.current;
      if (!c) return;
      if (c.state === "suspended") {
        try {
          await c.resume();
        } catch {
          return;
        }
      }
      if (cancelled) return;

      const playPhrase = () => {
        const ctx = ctxRef.current;
        if (!ctx || ctx.state !== "running") return;
        const base = ctx.currentTime;
        DOMBIRA_NOTES.forEach((freq, i) => {
          const osc = ctx.createOscillator();
          const g = ctx.createGain();
          osc.type = "triangle";
          osc.frequency.setValueAtTime(freq, base + i * 0.42);
          // Soft pluck envelope — fast attack, gentle decay.
          const t0 = base + i * 0.42;
          const peak = 0.05; // very quiet, ambient
          g.gain.setValueAtTime(0.0001, t0);
          g.gain.exponentialRampToValueAtTime(peak, t0 + 0.01);
          g.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.38);
          osc.connect(g);
          g.connect(ctx.destination);
          osc.start(t0);
          osc.stop(t0 + 0.42);
          osc.onended = () => {
            try {
              osc.disconnect();
              g.disconnect();
            } catch {
              /* noop */
            }
          };
        });
      };

      playPhrase();
      timerRef.current = window.setInterval(playPhrase, 4000);
    };
    void start();

    return () => {
      cancelled = true;
      if (timerRef.current !== null) {
        window.clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [enabled]);

  // Final cleanup — close the context when the variant unmounts entirely.
  useEffect(() => {
    return () => {
      if (timerRef.current !== null) {
        window.clearInterval(timerRef.current);
      }
      if (ctxRef.current) {
        try {
          void ctxRef.current.close();
        } catch {
          /* noop */
        }
      }
    };
  }, []);
}

// ─── Menu data — 6 signature dishes for opening night ────────────────────

// Menu copy lives in the dictionary; we render six rows by index.
const MENU_COUNT = 6;

// ─── Component ────────────────────────────────────────────────────────────

export function RestaurantOpeningVariant({ t, isDark, ctx }: VariantProps) {
  const reduce = usePrefersReducedMotion();
  const tt = useT();
  const { play, enabled: soundEnabled } = useSound();

  // Music toggle — independent from the global sound preference for UI
  // accents. We default OFF so guests aren't surprised on first paint.
  const [music, setMusic] = useState<boolean>(false);
  // Suspend the dombira theme if the user has muted UI sounds entirely —
  // they almost certainly want full silence.
  useDombiraTheme(music && soundEnabled);

  const toggleMusic = useCallback(() => {
    setMusic((prev) => {
      const next = !prev;
      // Sound trigger #4 — music toggle click.
      play(next ? "music-on" : "music-off");
      return next;
    });
  }, [play]);

  // Wrap RSVP setter so a "yes" plays a single dignified `pop`.
  const wrappedRsvp = {
    ...ctx.rsvp,
    setRsvp: (v: string | null) => {
      const before = ctx.rsvp.rsvp;
      ctx.rsvp.setRsvp(v);
      // Sound trigger #1 — RSVP chime on first yes.
      if (v === "yes" && before !== "yes") play("chime");
    },
  };

  // Palette — read from template colors so a future re-skin flows through.
  const cream = t.bg;
  const choco = t.ink;
  const brass = t.accent;
  const heroPhoto = t.photos[0];
  const interiorPhoto = t.photos[1] ?? t.photos[0];
  const chefPhoto = t.photos[2] ?? t.photos[0];

  return (
    <div className="v-r01">
      {/* ── Local CSS: signature keyframes + responsive lockdown ───── */}
      <style>{`
        @keyframes palovCharIn {
          from { opacity: 0; }
          to   { opacity: 1; }
        }
        @keyframes palovUnderline {
          to { stroke-dashoffset: 0; }
        }
        @media (prefers-reduced-motion: reduce) {
          .palov-char,
          .palov-underline {
            animation: none !important;
          }
        }

        .palov-menu-row {
          transition: background-color .25s ease-out, transform .25s ease-out;
        }
        .palov-menu-row:hover,
        .palov-menu-row:focus-within {
          background: ${brass}14;
          transform: translateX(2px);
        }
        @media (prefers-reduced-motion: reduce) {
          .palov-menu-row:hover,
          .palov-menu-row:focus-within { transform: none; }
        }

        .palov-music-btn {
          transition: transform .25s ease-out, box-shadow .25s ease-out;
        }
        .palov-music-btn:hover { transform: translateY(-1px); }
        @media (prefers-reduced-motion: reduce) {
          .palov-music-btn:hover { transform: none; }
        }

        .v-r01 a:focus-visible,
        .v-r01 button:focus-visible,
        .palov-menu-row:focus-visible {
          outline: 2px solid ${brass};
          outline-offset: 2px;
        }

        @media (max-width: 768px) {
          .v-r01 button, .v-r01 a { min-height: 44px; }
          .palov-hero {
            padding: 64px 18px 48px !important;
            min-height: auto !important;
          }
          .palov-hero h1 span { font-size: clamp(36px, 12vw, 96px) !important; }
          /* palov-hero-eyebrow: the hairline decorators (width:28 span) are
             inside a flex row; at narrow widths reduce letter-spacing so the
             full text fits without overflow. */
          .palov-hero-eyebrow {
            font-size: 9px !important;
            letter-spacing: 0.22em !important;
            flex-wrap: wrap !important;
            gap: 8px !important;
          }
          /* palov-hero-meta keyline: reduce letter-spacing and allow the
             date · venue string to wrap so it never exceeds the viewport. */
          .palov-hero-meta {
            font-size: 11px !important;
            letter-spacing: 0.18em !important;
            word-break: break-word !important;
          }
          .palov-menu-grid {
            grid-template-columns: 1fr !important;
            gap: 0 !important;
          }
          .palov-chef-card {
            grid-template-columns: 1fr !important;
            text-align: center !important;
          }
          .palov-chef-portrait {
            margin: 0 auto !important;
          }
          .palov-music-toggle {
            right: 16px !important;
            bottom: 16px !important;
          }
          .palov-section { padding: 64px 18px !important; }
        }
      `}</style>

      {/* ─── HERO ──────────────────────────────────────────────────── */}
      <section
        className="palov-hero"
        style={{
          minHeight: "100vh",
          padding: "120px 32px 96px",
          display: "grid",
          placeItems: "center",
          textAlign: "center",
          background: cream,
          color: choco,
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* Full-width grand restaurant photo — ghosted under the headline. */}
        <div
          aria-hidden
          style={{
            position: "absolute",
            inset: 0,
            backgroundImage: heroPhoto ? `url("${heroPhoto}")` : undefined,
            backgroundSize: "cover",
            backgroundPosition: "center",
            opacity: 0.32,
            filter: "saturate(0.85)",
          }}
        />
        {/* Cream wash so the type stays legible at any photo. */}
        <div
          aria-hidden
          style={{
            position: "absolute",
            inset: 0,
            background: `linear-gradient(180deg, ${cream}f5 0%, ${cream}cc 35%, ${cream}f5 100%)`,
          }}
        />
        {/* Inset chocolate hairline frame */}
        <div
          aria-hidden
          style={{
            position: "absolute",
            inset: "min(40px, 6vw)",
            border: `1px solid ${choco}33`,
            pointerEvents: "none",
          }}
        />

        <div style={{ position: "relative", maxWidth: 880, margin: "0 auto" }}>
          <div
            className="palov-hero-eyebrow"
            style={{
              fontFamily: "var(--font-ui)",
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: "0.5em",
              color: brass,
              marginBottom: 32,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 14,
            }}
          >
            <span style={{ width: 28, height: 1, background: brass }} />
            {tt("variant.r01.hero.eyebrow")}
            <span style={{ width: 28, height: 1, background: brass }} />
          </div>

          {/* SIGNATURE — letter-by-letter reveal + brass underline draw. */}
          <h1 style={{ margin: 0 }}>
            <SignatureName
              text={t.names}
              brass={brass}
              ink={choco}
              font={t.font || "var(--font-display)"}
              reduce={reduce}
            />
          </h1>

          <div
            style={{
              fontFamily: t.font || "var(--font-display)",
              fontStyle: "italic",
              fontSize: 22,
              letterSpacing: "0.04em",
              color: brass,
              marginTop: 24,
            }}
          >
            {tt("variant.r01.hero.tagline")}
          </div>

          {/* Date · venue keyline */}
          <div
            style={{
              margin: "44px auto 0",
              maxWidth: 360,
            }}
          >
            <span
              aria-hidden
              style={{ display: "block", height: 1, background: brass, opacity: 0.55, marginBottom: 14 }}
            />
            <div
              className="palov-hero-meta"
              style={{
                fontFamily: "var(--font-ui)",
                fontSize: 13,
                fontWeight: 700,
                letterSpacing: "0.34em",
                color: choco,
                fontFeatureSettings: '"tnum" 1, "lnum" 1',
              }}
            >
              {t.date.replace(/\./g, " · ")} · {t.venue.toUpperCase()}
            </div>
            <span
              aria-hidden
              style={{ display: "block", height: 1, background: brass, opacity: 0.55, marginTop: 14 }}
            />
          </div>
        </div>
      </section>

      <CountdownSection t={t} {...ctx.countdown} isDark={isDark} />

      <StorySection
        t={t}
        eyebrow={tt("variant.r01.story.eyebrow")}
        title={tt("variant.r01.story.title")}
        body={tt("variant.r01.story.body")}
      />

      {/* ─── UNIQUE — MENU PREVIEW ─────────────────────────────────── */}
      <section
        style={{
          padding: "120px 32px",
          borderTop: `1px solid ${choco}22`,
          background: `${brass}08`,
          position: "relative",
        }}
      >
        <div style={{ maxWidth: 880, margin: "0 auto" }}>
          <Reveal reduce={reduce} onShow={() => play("chime")}>
            <div style={{ textAlign: "center", marginBottom: 56 }}>
              <div
                style={{
                  fontFamily: "var(--font-ui)",
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: "0.4em",
                  color: brass,
                  marginBottom: 16,
                }}
              >
                {tt("variant.r01.menu.eyebrow")}
              </div>
              <h2
                style={{
                  fontFamily: t.font || "var(--font-display)",
                  fontStyle: "italic",
                  fontSize: "clamp(36px, 5vw, 56px)",
                  fontWeight: 400,
                  margin: 0,
                  color: choco,
                  letterSpacing: "-0.005em",
                }}
              >
                {tt("variant.r01.menu.title")}
              </h2>
              <div
                aria-hidden
                style={{
                  height: 1,
                  width: 64,
                  background: brass,
                  margin: "20px auto 0",
                }}
              />
            </div>
          </Reveal>

          <div
            className="palov-menu-grid"
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(2, 1fr)",
              gap: "0 56px",
            }}
          >
            {Array.from({ length: MENU_COUNT }).map((_, i) => {
              const itemName = tt(`variant.r01.menu.${i}.name`);
              const itemNote = tt(`variant.r01.menu.${i}.note`);
              const itemPrice = tt(`variant.r01.menu.${i}.price`);
              return (
              <Reveal key={i} reduce={reduce} delay={Math.min(i * 70, 280)}>
                <button
                  type="button"
                  className="palov-menu-row"
                  onClick={() => play("tap")}
                  onMouseEnter={() => play("hover")}
                  style={{
                    width: "100%",
                    padding: "22px 20px",
                    borderTop: "none",
                    borderLeft: "none",
                    borderRight: "none",
                    borderBottom: `1px dashed ${brass}55`,
                    display: "grid",
                    gridTemplateColumns: "1fr auto",
                    alignItems: "baseline",
                    gap: 16,
                    cursor: "pointer",
                    background: "transparent",
                    color: "inherit",
                    textAlign: "left",
                    font: "inherit",
                  }}
                >
                  <div>
                    <div
                      style={{
                        fontFamily: t.font || "var(--font-display)",
                        fontStyle: "italic",
                        fontSize: 24,
                        fontWeight: 400,
                        color: choco,
                        letterSpacing: "0.005em",
                      }}
                    >
                      {itemName}
                    </div>
                    {itemNote && (
                      <div
                        style={{
                          fontFamily: "var(--font-body)",
                          fontSize: 13,
                          color: `${choco}99`,
                          marginTop: 4,
                          fontStyle: "italic",
                        }}
                      >
                        {itemNote}
                      </div>
                    )}
                  </div>
                  <div
                    aria-hidden
                    style={{
                      flex: 1,
                      height: 1,
                      background: `${brass}66`,
                      alignSelf: "center",
                      display: "none",
                    }}
                  />
                  <div
                    style={{
                      fontFamily: "var(--font-ui)",
                      fontSize: 14,
                      fontWeight: 600,
                      letterSpacing: "0.06em",
                      color: brass,
                      fontFeatureSettings: '"tnum" 1, "lnum" 1',
                      whiteSpace: "nowrap",
                    }}
                  >
                    {itemPrice}
                  </div>
                </button>
              </Reveal>
              );
            })}
          </div>

          <div
            style={{
              marginTop: 40,
              textAlign: "center",
              fontFamily: "var(--font-body)",
              fontSize: 14,
              fontStyle: "italic",
              color: `${choco}88`,
            }}
          >
            {tt("variant.r01.menu.disclaimer")}
          </div>
        </div>
      </section>

      {/* ─── UNIQUE — CHEF SECTION ─────────────────────────────────── */}
      <section
        style={{
          padding: "120px 32px",
          borderTop: `1px solid ${choco}22`,
          background: cream,
        }}
      >
        <div style={{ maxWidth: 760, margin: "0 auto" }}>
          <Reveal reduce={reduce}>
            <div
              className="palov-chef-card"
              style={{
                display: "grid",
                gridTemplateColumns: "200px 1fr",
                gap: 40,
                alignItems: "center",
              }}
              tabIndex={0}
              onMouseEnter={() => play("hover")}
              onFocus={() => play("hover")}
            >
              {/* Brass-bordered circular chef portrait */}
              <div
                className="palov-chef-portrait"
                style={{
                  width: 200,
                  height: 200,
                  borderRadius: "50%",
                  padding: 6,
                  background: `linear-gradient(140deg, ${brass}, ${choco})`,
                  boxShadow: `0 6px 18px ${choco}33`,
                  flexShrink: 0,
                }}
              >
                <div
                  style={{
                    width: "100%",
                    height: "100%",
                    borderRadius: "50%",
                    overflow: "hidden",
                    backgroundColor: cream,
                    backgroundImage: chefPhoto ? `url("${chefPhoto}")` : undefined,
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                    border: `2px solid ${cream}`,
                  }}
                />
              </div>

              <div>
                <div
                  style={{
                    fontFamily: "var(--font-ui)",
                    fontSize: 11,
                    fontWeight: 700,
                    letterSpacing: "0.4em",
                    color: brass,
                    marginBottom: 12,
                  }}
                >
                  {tt("variant.r01.chef.eyebrow")}
                </div>
                <h3
                  style={{
                    fontFamily: t.font || "var(--font-display)",
                    fontStyle: "italic",
                    fontSize: 36,
                    fontWeight: 400,
                    margin: 0,
                    color: choco,
                    letterSpacing: "-0.005em",
                  }}
                >
                  {tt("variant.r01.chef.name")}
                </h3>
                <div
                  aria-hidden
                  style={{
                    width: 48,
                    height: 1,
                    background: brass,
                    margin: "16px 0",
                  }}
                />
                <div
                  style={{
                    fontFamily: "var(--font-ui)",
                    fontSize: 12,
                    fontWeight: 600,
                    letterSpacing: "0.18em",
                    color: `${choco}aa`,
                    marginBottom: 14,
                    textTransform: "uppercase",
                  }}
                >
                  {tt("variant.r01.chef.experience")}
                </div>
                <p
                  style={{
                    fontFamily: "var(--font-body)",
                    fontSize: 16,
                    lineHeight: 1.7,
                    color: `${choco}cc`,
                    margin: 0,
                    fontStyle: "italic",
                  }}
                >
                  {tt("variant.r01.chef.bio")}
                </p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <Reveal reduce={reduce}>
        <GallerySection t={t} />
      </Reveal>

      <Reveal reduce={reduce}>
        <ScheduleSection
          t={t}
          items={[
            ["18:00", tt("variant.r01.schedule.0.event")],
            ["18:30", tt("variant.r01.schedule.1.event")],
            ["19:00", tt("variant.r01.schedule.2.event")],
            ["20:30", tt("variant.r01.schedule.3.event")],
            ["21:30", tt("variant.r01.schedule.4.event")],
            ["23:00", tt("variant.r01.schedule.5.event")],
          ]}
        />
      </Reveal>

      <Reveal reduce={reduce}>
        <MapSection
          t={t}
          isDark={isDark}
          addressLine={tt("variant.r01.map.address")}
        />
      </Reveal>

      <Reveal reduce={reduce}>
        <RsvpSection
          t={t}
          isDark={isDark}
          rsvp={ctx.rsvp.rsvp}
          setRsvp={wrappedRsvp.setRsvp}
          guests={ctx.rsvp.guests}
          setGuests={ctx.rsvp.setGuests}
          guestName={ctx.rsvp.guestName}
          setGuestName={ctx.rsvp.setGuestName}
          slug={ctx.slug}
          label={tt("variant.r01.rsvp.label")}
        />
      </Reveal>

      <Reveal reduce={reduce}>
        <GuestbookSection
          t={t}
          isDark={isDark}
          wishes={ctx.guestbook.wishes}
          setWishes={ctx.guestbook.setWishes}
          guestName={ctx.guestbook.guestName}
          setGuestName={ctx.guestbook.setGuestName}
          wish={ctx.guestbook.wish}
          setWish={ctx.guestbook.setWish}
          slug={ctx.slug}
        />
      </Reveal>

      <Reveal reduce={reduce}>
        <ShareSection t={t} />
      </Reveal>

      <GuestFooter t={t} />

      {/* ─── Floating do'mbira music toggle ─────────────────────────── */}
      <button
        type="button"
        className="palov-music-btn palov-music-toggle"
        onClick={toggleMusic}
        aria-pressed={music}
        aria-label={music ? "Pause music" : "Play music"}
        style={{
          position: "fixed",
          right: 24,
          bottom: 24,
          zIndex: 50,
          width: 56,
          height: 56,
          borderRadius: "50%",
          border: `1px solid ${choco}`,
          background: music ? brass : cream,
          color: music ? cream : choco,
          cursor: "pointer",
          display: "grid",
          placeItems: "center",
          fontFamily: "var(--font-ui)",
          fontSize: 18,
          boxShadow: `0 6px 14px ${choco}33`,
          padding: 0,
        }}
      >
        <svg width="22" height="22" viewBox="0 0 24 24" aria-hidden>
          {music ? (
            // Pause glyph
            <g fill="currentColor">
              <rect x="6" y="5" width="4" height="14" rx="1" />
              <rect x="14" y="5" width="4" height="14" rx="1" />
            </g>
          ) : (
            // Music note glyph — folksy do'mbira nod
            <g fill="currentColor">
              <path d="M12 4 L18 3 L18 14 A3 3 0 1 1 16 11 L16 6 L14 6.4 L14 16 A3 3 0 1 1 12 13 Z" />
            </g>
          )}
        </svg>
      </button>
    </div>
  );
}
